import React from "react";
import { Cpu, Zap, Sparkles, Smile, Brain, Wand2, Compass, Feather, FlaskConical, Rocket } from "lucide-react";

/**
 * Full 12-brain catalog mirroring backend `providers.BRAINS`.
 * Legacy 4-key aliases at the bottom keep old conversations/agents resolving.
 */
export const MODELS = {
  // OpenAI
  "gpt-5-1": {
    name: "GPT-5.1", family: "OpenAI", tier: "premium",
    color: "#10A37F", bg: "#D1FAE5", border: "#047857",
    icon: Brain, personality: "Deep reasoner",
  },
  "gpt-4o-mini": {
    name: "GPT-4o Mini", family: "OpenAI", tier: "fast",
    color: "#10A37F", bg: "#D1FAE5", border: "#047857",
    icon: Zap, personality: "Cheap default",
  },
  // Anthropic
  "claude-sonnet-4-6": {
    name: "Sonnet 4.6", family: "Anthropic", tier: "premium",
    color: "#D97706", bg: "#FEF3C7", border: "#B45309",
    icon: Feather, personality: "Best writer",
  },
  "claude-haiku-4-5": {
    name: "Haiku 4.5", family: "Anthropic", tier: "fast",
    color: "#D97706", bg: "#FEF3C7", border: "#B45309",
    icon: Wand2, personality: "Fast Claude",
  },
  // Google
  "gemini-2-5-pro": {
    name: "Gemini 2.5 Pro", family: "Google", tier: "premium",
    color: "#4285F4", bg: "#DBEAFE", border: "#1D4ED8",
    icon: Compass, personality: "2M ctx",
  },
  "gemini-2-5-flash-lite": {
    name: "Flash Lite", family: "Google", tier: "fast",
    color: "#4285F4", bg: "#DBEAFE", border: "#1D4ED8",
    icon: Rocket, personality: "Cheapest",
  },
  // DeepSeek
  "deepseek-chat": {
    name: "DeepSeek V3", family: "DeepSeek", tier: "balanced",
    color: "#3B82F6", bg: "#DBEAFE", border: "#2563EB",
    icon: Cpu, personality: "Math + code",
  },
  // Qwen
  "qwen-72b": {
    name: "Qwen 2.5 72B", family: "Qwen", tier: "balanced",
    color: "#EF4444", bg: "#FEE2E2", border: "#DC2626",
    icon: Zap, personality: "Bold & quick",
  },
  // Moonshot / Kimi
  "kimi-k2": {
    name: "Kimi K2", family: "Moonshot", tier: "balanced",
    color: "#10B981", bg: "#D1FAE5", border: "#059669",
    icon: Sparkles, personality: "Creative prose",
  },
  // Meta
  "llama-4-maverick": {
    name: "Llama 4 Maverick", family: "Meta", tier: "premium",
    color: "#F59E0B", bg: "#FEF3C7", border: "#D97706",
    icon: Smile, personality: "Warm & open",
  },
  "llama-3-3-70b": {
    name: "Llama 3.3 70B", family: "Meta", tier: "balanced",
    color: "#F59E0B", bg: "#FEF3C7", border: "#D97706",
    icon: Smile, personality: "Open-weight",
  },
  // Mistral
  "mistral-large": {
    name: "Mistral Small 3.2", family: "Mistral", tier: "balanced",
    color: "#FA5D0F", bg: "#FFEDD5", border: "#C2410C",
    icon: FlaskConical, personality: "EU option",
  },
};

// Legacy short aliases used by pre-existing conversations/agents.
MODELS.deepseek = MODELS["deepseek-chat"];
MODELS.qwen     = MODELS["qwen-72b"];
MODELS.kimi     = MODELS["kimi-k2"];
MODELS.llama    = MODELS["llama-3-3-70b"];

// Stable canonical order (used by Chat/Compare/Research chip pickers).
// Excludes legacy 4 aliases so we don't render duplicates.
export const MODEL_KEYS = [
  "gpt-5-1", "claude-sonnet-4-6", "gemini-2-5-pro",
  "gpt-4o-mini", "claude-haiku-4-5", "gemini-2-5-flash-lite",
  "deepseek-chat", "qwen-72b", "kimi-k2",
  "llama-4-maverick", "llama-3-3-70b", "mistral-large",
];

export function ModelBadge({ id, size = "md", showName = true, ...rest }) {
  const m = MODELS[id];
  if (!m) return null;
  const Icon = m.icon;
  const pad = size === "sm" ? "px-2 py-1 text-xs" : size === "lg" ? "px-4 py-2 text-base" : "px-3 py-1.5 text-sm";
  return (
    <span
      data-testid={`model-badge-${id}`}
      className={`inline-flex items-center gap-1.5 rounded-full border-2 font-bold font-heading ${pad}`}
      style={{ backgroundColor: m.bg, borderColor: m.border, color: m.border }}
      {...rest}
    >
      <Icon size={size === "sm" ? 12 : 16} strokeWidth={2.5} />
      {showName && m.name}
    </span>
  );
}

export function ModelChip({ id, selected, onClick, disabled }) {
  const m = MODELS[id];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <button
      type="button"
      data-testid={`model-chip-${id}`}
      disabled={disabled}
      onClick={onClick}
      className="flex items-center gap-2 px-3.5 py-2 rounded-xl border-2 font-heading font-semibold text-sm transition-all disabled:opacity-50"
      style={{
        backgroundColor: selected ? m.color : "#fff",
        color: selected ? "#fff" : m.border,
        borderColor: m.border,
        boxShadow: selected ? `3px 3px 0 ${m.border}` : "none",
        transform: selected ? "translate(-1px, -1px)" : "none",
      }}
    >
      <Icon size={16} strokeWidth={2.5} />
      {m.name}
    </button>
  );
}
